import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ApplyLogEntry, LogLevel, WizardPreview } from "@/types/wizard";

interface WizardSessionValue {
  previewResult: WizardPreview | null;
  setPreviewResult: (value: WizardPreview | null) => void;
  applyResult: WizardPreview | null;
  setApplyResult: (value: WizardPreview | null) => void;
  applyLogs: ApplyLogEntry[];
  resetApplyLogs: () => void;
  appendApplyLog: (entry: { level: LogLevel; message: string; stage?: string }) => void;
  resetSession: () => void;
}

const WizardSessionContext = createContext<WizardSessionValue | null>(null);

export function WizardSessionProvider({ children }: { children: React.ReactNode }) {
  const [previewResult, setPreviewResult] = useState<WizardPreview | null>(null);
  const [applyResult, setApplyResult] = useState<WizardPreview | null>(null);
  const [applyLogs, setApplyLogs] = useState<ApplyLogEntry[]>([]);

  const resetApplyLogs = useCallback(() => setApplyLogs([]), []);

  const appendApplyLog = useCallback(
    ({ level, message, stage }: { level: LogLevel; message: string; stage?: string }) => {
      setApplyLogs((prev) => [
        ...prev,
        {
          id: `${Date.now()}-${prev.length}-${Math.random().toString(36).slice(2, 7)}`,
          level,
          message,
          stage,
          time: new Date().toLocaleTimeString(),
        },
      ]);
    },
    [],
  );

  const resetSession = useCallback(() => {
    setPreviewResult(null);
    setApplyResult(null);
    setApplyLogs([]);
  }, []);

  const value = useMemo<WizardSessionValue>(
    () => ({
      previewResult,
      setPreviewResult,
      applyResult,
      setApplyResult,
      applyLogs,
      resetApplyLogs,
      appendApplyLog,
      resetSession,
    }),
    [previewResult, applyResult, applyLogs, resetApplyLogs, appendApplyLog, resetSession],
  );

  return <WizardSessionContext.Provider value={value}>{children}</WizardSessionContext.Provider>;
}

export function useWizardSession(): WizardSessionValue {
  const ctx = useContext(WizardSessionContext);
  if (!ctx) {
    throw new Error("useWizardSession must be used within a WizardSessionProvider.");
  }
  return ctx;
}
